import React, { useState, useEffect } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { TextField } from "@project-stories/elements/TextField/TextField";
import Select from '@src/stories/elements/Select/Select';


type Tab = {
    title: string;
    link: string;
};

interface BlogArticlesHeaderProps {
    initialArticles: any;
    settings: any;
    query: string;
    setQuery: (q: string) => void;
    allTags: string[];
    setTag: (tag: string) => void;
    tag: string;
}

export const BlogArticlesHeader: React.FC<BlogArticlesHeaderProps> = ({
    initialArticles, settings, query, setQuery, allTags, setTag, tag
}) => {
    const isMobile = useMobileBreakpoint(settings?.globals?.mobile_breakpoint);
    const [isSticky, setIsSticky] = useState(false);
    const [rightOffset, setRightOffset] = useState(0);
    const {
        globals: { blog_tabs = [] },
        section = {}
    } = settings;

    const section_blog_tabs = [1, 2, 3]
        .map(i => ({
            title: section[`blog_tab_title_${i}`],
            link: section[`blog_tab_link_${i}`]
        }))
        .filter(tab => tab.title && tab.link);

    const current_blog_tabs = section_blog_tabs.length > 0 ? section_blog_tabs : blog_tabs;
 

        useEffect(() => {
            const handleScroll = () => {
                const headerElement = document.querySelector('.header-container');
                const containerElement = document.querySelector('.header-menu-container');
                const pageHeaderElement = document.querySelector('.page-header-section');
                if (pageHeaderElement && headerElement && containerElement) {
                    const headerHeight = headerElement.offsetHeight;
                    const stickyTop = headerHeight + pageHeaderElement.offsetHeight  + 120;
                    const containerWidth = containerElement.offsetWidth;
                    const windowWidth = window.innerWidth;
                    const rightOffset = (windowWidth - containerWidth) / 2;
                    setIsSticky(window.scrollY > stickyTop);
                    setRightOffset(rightOffset);
                }
            };

            window.addEventListener('scroll', handleScroll);
            return () => window.removeEventListener('scroll', handleScroll);
        }, []);

      const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
      };

      const handleTagChange = (selectedOption) => {
        let newTag = "";
        // If Select passes the whole option object
        if (selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption) {
            newTag = selectedOption.value;
            setTag(newTag);
        } else {
            // If Select passes just the value
            newTag = selectedOption || "";
            setTag(newTag);
        }
    
        // Update the URL query string
        if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            if (newTag) {
                url.searchParams.set("tag", newTag);
            } else {
                url.searchParams.delete("tag");
            }
            window.history.replaceState({}, '', url.toString());
        }
    };
    

    // Prepare options for Select
    const tagOptions = allTags?.map(tag => ({
        name: tag,
        value: tag
    })) || [];

    // Pill navigation logic
    const tabs: Tab[] = Array.isArray(current_blog_tabs)
        ? current_blog_tabs.filter(tab => tab.title && tab.link)
        : [];

    const currentHandle = typeof window !== "undefined"
        ? window.location.pathname.split('/').filter(Boolean).pop() || ""
        : "";

    const isActiveTab = (link: string) => {
        const tabHandle = link.split('/').filter(Boolean).pop() || "";
        return currentHandle.toLowerCase() === tabHandle.toLowerCase();
    };

    const renderPill = () => { 
        return  tabs.length > 0 && (
            <div
                className={`blog-articles-header-pill flex gap-[6px] transition-all duration-200 ${
                    isSticky
                        ? 'fixed bg-transparent z-40 justify-center lg:justify-end lg:px-0'
                        : 'w-full lg:justify-end'
                }`}
                style={{
                    top: isSticky
                        ? `${document.querySelector('.header-container')?.offsetHeight + 20}px`
                        : 'auto',
                    ...(isSticky && !isMobile && {
                        right: `${rightOffset}px`,
                        maxWidth: 'var(--container-width, 1200px)', // adjust as needed
                        width: '100%',
                    }),
                    ...(!isSticky && {
                        right: 'auto',
                        maxWidth: 'none',
                    }),
                    // Center pill on mobile when sticky
                    ...(isSticky && isMobile
                        ? {
                            left: '50%',
                            transform: 'translateX(-50%)',
                            right: 'auto',
                            maxWidth: '90vw',
                            width: 'max-content',
                        }
                        : {}
                    ),
                }}
            >
                <div className={`flex bg-white w-full gap-[6px] border-black border rounded-full p-1 justify-end lg:w-fit  ${
                    isSticky ? 'shadow-[0px_12px_16px_-4px_rgba(0,0,0,0.20),0px_4px_6px_-2px_rgba(0,0,0,0.03)]' : ''
                }`}>
                    {tabs.map((tab, index) => (
                        <a
                            key={index}
                            href={tab.link}
                            className={`min-w-[104.2px] w-full lg:min-w-[126px] text-center flex items-center justify-center h-[50px] rounded-full transition-all duration-200 b3--bold ${
                                isActiveTab(tab.link) 
                                    ? 'border border-black bg-black text-white' 
                                    : 'hover:bg-black hover:text-white'
                            }`}
                        >
                            {tab.title}
                        </a>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className="blog-articles-header flex flex-col gap-4 lg:gap-6 w-full">
            {/* Pills on top for mobile */}
            {isMobile && renderPill()}
    
            <div className="flex flex-col gap-2 lg:gap-4">
                <span className="h4">Categories</span>
            </div>
    
            <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 mb-6 lg:mb-0">
                <div className="w-full lg:w-1/2">
                    <TextField
                        id="blog-search"
                        name="q"
                        type="default-input"
                        iconLeft={'icon-search'}
                        className="blog-articles-search-input transition-all duration-300 expanded-input"
                        value={query}
                        placeholder="Search"
                        onChange={handleInputChange}
                    />
                </div>
                <div className="w-full lg:w-1/2">
                    <Select
                        options={tagOptions}
                        onChange={handleTagChange}
                        selected={tag}
                        allowDeselect={true}
                        borderRadius={12}
                        placeholder="View All"
                    />
                </div>
                {/* Pills on right for desktop */}
                {!isMobile && renderPill()}
            </div>
        </div>
    );
};
